import React, { useState } from 'react';
import { IconName, Icon } from 'app/common/components/controls';
import { Tooltip, TooltipPrimitiveLight } from '@trello/nachos';
import { Spinner } from 'app/src/components/Spinner';
import classNames from 'classnames';
import { Overlay, OverlayBackground } from 'app/src/components/Overlay';
import styles from './PlanSelection.less';
import { forNamespace } from 'app/common/lib/util/i18n';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { OrganizationFeatures } from '@trello/product-features';
import { useFreeTrial } from './useFreeTrial';
import { UpsellOverlay } from './UpsellOverlay';
import { formatMoney } from 'app/scripts/lib/format/money';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { PlanSelectionOld } from './PlanSelectionOld';
import { useFreeTrialEligibilityRules } from './useFreeTrialEligibilityRules';
import { Analytics } from '@trello/atlassian-analytics';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary/ErrorBoundary';
import { Feature } from 'app/scripts/debug/constants';

export enum PlanSelectionType {
  TryBC = 'try-business-class',
  HaveBC = 'have-business-class',
}

const format = forNamespace('upgrade-prompt-plan-selection-consolidated');
const formatPrompt = forNamespace('upgrade prompt plan selection');

const getPlanFeatureStrings = (plan: string, feature: string) => {
  const namespace = [plan, 'features', feature];

  return {
    title: plan === 'free-team' ? null : format([...namespace, 'title']),
    content: format([...namespace, 'content']),
    tooltip:
      plan === 'free-team' || feature === 'boards'
        ? null
        : format([...namespace, 'tooltip']),
  };
};

// exported for testing
export const bcPlanSelectionList = [
  {
    icon: IconName.Board,
    ...getPlanFeatureStrings('bc-team', 'boards'),
  },
  {
    icon: IconName.PowerUp,
    ...getPlanFeatureStrings('bc-team', 'pups'),
  },
  {
    icon: IconName.ButlerBot,
    ...getPlanFeatureStrings('bc-team', 'butler'),
  },
  {
    icon: IconName.Subscribe,
    ...getPlanFeatureStrings('bc-team', 'admin'),
  },
  {
    icon: IconName.Heart,
    ...getPlanFeatureStrings('bc-team', 'support'),
  },
  {
    icon: IconName.Star,
    ...getPlanFeatureStrings('bc-team', 'more'),
  },
];

const freeTeamSelectionList = [
  {
    icon: IconName.Board,
    ...getPlanFeatureStrings('free-team', 'boards'),
  },
  {
    icon: IconName.PowerUp,
    ...getPlanFeatureStrings('free-team', 'pups'),
  },
  {
    icon: IconName.ButlerBot,
    ...getPlanFeatureStrings('free-team', 'butler'),
  },
];

const renderTooltip = (tooltip: string | null, target: React.ReactNode) =>
  tooltip ? (
    <Tooltip content={tooltip} component={TooltipPrimitiveLight}>
      {target}
    </Tooltip>
  ) : (
    target
  );

interface PlanSelectionProps {
  trialLength?: number;
  onContinue(): void;
  onSkip?(): void;
  ctaLink?: string;
  orgId: string;
  showPlanButtons?: boolean;
  startTrialOnClick?: boolean;
  setFreeTrial?: () => void;
}

const afterTrial = format('after-trial', {
  price: `$${formatMoney(
    OrganizationFeatures.getPrice(
      OrganizationFeatures.currentProducts.yearly,
    )! / 12,
  )}`,
  downgradeOption: (
    <a
      key="after-trial-downgrade"
      target="_blank"
      href="https://help.trello.com/article/836-cancel-trello-business-class"
    >
      {format('after-trial-downgrade')}
    </a>
  ),
});

export const PlanSelection: React.FunctionComponent<PlanSelectionProps> = ({
  trialLength = 14,
  onContinue,
  onSkip = () => {},
  orgId,
  showPlanButtons,
  ctaLink,
  startTrialOnClick = true,
  setFreeTrial,
}) => {
  const { isLoading, startFreeTrial } = useFreeTrial({
    orgId,
    onFreeTrial: setFreeTrial,
  });
  const { hasCredit, isEligible, loading } = useFreeTrialEligibilityRules(
    orgId,
  );

  const type = hasCredit ? PlanSelectionType.HaveBC : PlanSelectionType.TryBC;

  const isFreeTrialExistingActive = useFeatureFlag(
    'btg.free-trial-existing-teams',
    false,
  );

  if (loading) {
    return null;
  }

  const onClickCta = async () => {
    if (isEligible) {
      if (startTrialOnClick) {
        Analytics.sendTrackEvent({
          action: 'accepted',
          actionSubject: 'freeTrial',
          source: 'planSelectionModal',
          containers: {
            organization: {
              id: orgId,
            },
          },
        });
        await startFreeTrial();
      }
      onContinue();
    } else {
      window.location.assign('/business-class');
    }
  };

  const learnMoreBCButtonWithTracking = async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'learnMoreAboutBCButton',
      source: 'planSelectionModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
  };

  Analytics.sendScreenEvent({
    name: 'planSelectionModal',
    containers: {
      organization: {
        id: orgId,
      },
    },
  });

  if (!isFreeTrialExistingActive) {
    return (
      <PlanSelectionOld
        onContinue={onContinue}
        onSkip={onSkip}
        ctaLink={ctaLink}
        showPlanButtons={showPlanButtons}
      />
    );
  }

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.FreeTrialExistingTeam,
      }}
    >
      <div className={styles.heading}>
        <h1>
          <img
            src={require('resources/images/moonshot/bc-sparkle.svg')}
            alt=""
            aria-hidden="true"
          />
          {format([type, 'headline'], { dayCount: trialLength })}
        </h1>
        <h2>{format([type, 'description'], { dayCount: trialLength })}</h2>
      </div>

      <div className={styles.bodyWrapper}>
        <div className={styles.freePlan}>
          <h2>{format(['free-team', 'headline'])}</h2>
          <ul>
            {freeTeamSelectionList.map(({ icon, content }, index) => (
              <li key={index} className={styles.featureItem}>
                <Icon name={icon} medium className={styles.featureIcon} />
                <span className={styles.featureContent}>{content}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.bcPlan}>
          <div className={styles.recommendedLabel}>
            {format([type, 'tag'], { dayCount: trialLength })}
          </div>
          <h2>{format(['bc-team', 'headline'])}</h2>
          <ul>
            {bcPlanSelectionList.map(
              ({ icon, title, content, tooltip }, index) => (
                <li
                  key={index}
                  className={classNames(
                    styles.featureItem,
                    !!tooltip && styles.tooltipTarget,
                  )}
                >
                  {renderTooltip(
                    tooltip,
                    <div className={styles.featureItemWrapper}>
                      <Icon name={icon} medium className={styles.featureIcon} />
                      <div>
                        <span className={styles.featureTitle}>
                          {title} {''}
                          <span className={styles.featureContent}>
                            {content}
                          </span>
                        </span>
                      </div>
                    </div>,
                  )}
                </li>
              ),
            )}
          </ul>
        </div>
      </div>
      <div className={styles.actions}>
        <button
          onClick={onClickCta}
          className={styles.ctaButton}
          disabled={isLoading}
        >
          {isLoading ? (
            <Spinner centered small wrapperClassName={styles.spinner} />
          ) : startTrialOnClick ? (
            format([type, 'cta'], { dayCount: trialLength })
          ) : (
            formatPrompt('start bc')
          )}
        </button>
        <div className={styles.trustedBy}>
          <span>{format('trusted-by-teams-at')}</span>
          <img
            src={require('resources/images/upgrade-prompts/pinterest.svg')}
            alt="Pinterest"
            width="22"
          />
          <img
            src={require('resources/images/upgrade-prompts/bose.svg')}
            alt="Bose"
            width="91"
          />
          <img
            src={require('resources/images/upgrade-prompts/fortnite.svg')}
            alt="Fortnite"
            width="58"
          />
          <img
            src={require('resources/images/upgrade-prompts/peloton.svg')}
            alt="Peloton"
            width="85"
          />
        </div>
        <p className={styles.afterTrial}>{afterTrial}</p>
        {type === PlanSelectionType.TryBC && (
          <a
            onClick={() => learnMoreBCButtonWithTracking()}
            href="/business-class"
            className={styles.secondaryCta}
          >
            {format([type, 'secondary-cta'])}
          </a>
        )}
      </div>
    </ErrorBoundary>
  );
};

export const PlanSelectionOverlay: React.FunctionComponent<
  PlanSelectionProps & {
    onClose(): void;
  }
> = ({
  onContinue,
  onSkip = () => {},
  onClose,
  ctaLink,
  orgId,
  showPlanButtons,
  setFreeTrial,
  trialLength,
}) => {
  const { isEligible, isTrialActive, loading } = useFreeTrialEligibilityRules(
    orgId,
  );

  const [isViewingPlan, setIsViewingPlan] = useState(false);
  const isFreeTrialExistingActive = useFeatureFlag(
    'btg.free-trial-existing-teams',
    false,
  );

  const goBackWithTracking = async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'backButton',
      source: 'planSelectionModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
    setIsViewingPlan(false);
  };

  const closeOverlayWithTracking = async () => {
    Analytics.sendClosedComponentEvent({
      componentType: 'modal',
      componentName: 'freeTrialModal',
      source: 'planSelectionModal',
    });
    onClose();
  };

  if (loading) {
    return null;
  }

  let children = (
    <PlanSelectionOld
      onContinue={onContinue}
      onSkip={onSkip || (() => {})}
      ctaLink={ctaLink}
      showPlanButtons={showPlanButtons}
    />
  );

  if (isFreeTrialExistingActive) {
    if (isEligible) {
      if (isViewingPlan) {
        children = (
          <PlanSelection
            orgId={orgId}
            onContinue={() => {
              onClose();
              setIsViewingPlan(false);
            }}
            setFreeTrial={setFreeTrial}
            trialLength={trialLength}
          />
        );
      } else {
        children = (
          <UpsellOverlay
            orgId={orgId}
            onLearnMore={() => setIsViewingPlan(true)}
            onClose={() => {}}
            setFreeTrial={setFreeTrial}
          />
        );
      }
    } else if (isTrialActive) {
      children = (
        <PlanSelection
          orgId={orgId}
          onContinue={onContinue}
          trialLength={trialLength}
        />
      );
    }
  }

  return (
    <Overlay onClose={onClose} background={OverlayBackground.DARK}>
      <div
        className={classNames(
          styles.overlay,
          isFreeTrialExistingActive && styles.overlayExistingTeams,
          (isTrialActive || isViewingPlan) && styles.overlayViewingPlan,

          // TODO: Remove whenever we get rid of the old plan selection
          !isFreeTrialExistingActive && styles.overlayOldPlanSelection,
        )}
        tabIndex={-1}
        ref={(el) => {
          if (el) {
            // bring focus to overlay container
            el.focus();
          }
        }}
      >
        {isFreeTrialExistingActive && isViewingPlan && (
          <button
            className={styles.back}
            title="Todo"
            aria-label="Todo"
            onClick={() => goBackWithTracking()}
          >
            <Icon name={IconName.Back} large />
          </button>
        )}
        <button
          className={styles.close}
          title={formatPrompt('close')}
          aria-label={formatPrompt('close')}
          onClick={() => closeOverlayWithTracking()}
        >
          <Icon name={IconName.Close} large />
        </button>
        {children}
      </div>
    </Overlay>
  );
};

export const PlanSelectionOverlayConnected: React.FunctionComponent<
  PlanSelectionProps & {
    onClose(): void;
  }
> = (props) => (
  <ComponentWrapper>
    <PlanSelectionOverlay {...props} />
  </ComponentWrapper>
);
